"use client";

import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { CartIcon } from "@/components/shop/CartIcon";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { cn } from "@/lib/utils";
function HeaderMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="hidden md:flex gap-1">
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
        <div className="h-9 w-16 bg-muted/20 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false} className="hidden md:flex">
      <NavigationMenuList>
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="hover:!bg-transparent focus:!bg-transparent relative group px-4 py-2 text-sm font-medium transition-all duration-fast hover:text-accent focus:text-accent">
                {item.label}
                <span className="absolute bottom-0 left-1/2 h-[2px] w-0 bg-accent transition-all duration-medium ease-smooth group-hover:w-full group-hover:left-0 group-focus:w-full group-focus:left-0" />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  const [isOpen, setIsOpen] = useState(false);
  if (loading) {
    return <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" disabled>
        <Menu className="size-5" />
      </Button>;
  }
  return <>
      <Button variant="ghost" size="icon" className="md:hidden hover:!bg-transparent focus:!bg-transparent" onClick={() => setIsOpen(!isOpen)} aria-label="Toggle menu">
        {isOpen ? <X className="size-5" /> : <Menu className="size-5" />}
      </Button>

      {isOpen && <div className="absolute top-full left-0 w-full bg-white/95 backdrop-blur-md border-b border-muted/20 shadow-medium md:hidden z-50">
          <nav className="container mx-auto px-4 py-4">
            <ul className="flex flex-col gap-2">
              {menuItems.map((item, i) => <li key={item.id} data-index={i}>
                  <Link to={item.href || "#"} className="block px-4 py-3 rounded-md text-sm font-medium hover:bg-accent/10 transition-colors duration-fast" onClick={() => setIsOpen(false)}>
                    {item.label}
                  </Link>
                </li>)}
            </ul>
          </nav>
        </div>}
    </>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <header id="header" className={cn("fixed top-0 left-0 right-0 z-50 transition-all duration-medium ease-smooth", isScrolled ? "h-[60px] bg-white/90 backdrop-blur-md shadow-soft" : "h-[80px] bg-white")}>
      <div className="container mx-auto px-4 h-full flex items-center justify-between">
        <Link to="/" className="flex-shrink-0">
          <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1542868/img/visit_france_logo.png" alt="Visit-France-4-Less" className="h-7" />
        </Link>

        <MenuProvider menu_id="51" className="flex items-center justify-center flex-1">
          <HeaderMenuConsumer />
        </MenuProvider>

        <div className="flex items-center gap-3">
          <Button asChild className="hidden sm:inline-flex hover:!bg-accent-hover focus:!bg-accent-hover hover:scale-[1.03] active:scale-100 transition-all duration-fast">
            <Link to="/#">Download Free Survival Kit</Link>
          </Button>

          <CartIcon variant="ghost" size="icon" className="hover:!bg-transparent focus:!bg-transparent" iconSize="size-5" />

          <MenuProvider menu_id="51" className="md:hidden">
            <MobileMenuConsumer />
          </MenuProvider>
        </div>
      </div>
    </header>;
}